import React, { useEffect, useMemo, useState } from "react";
import CustomTab from "@/components/dataTableComponents/CustomTab";
import { Formik, Form, ErrorMessage } from "formik";
import { Box, Button, CircularProgress } from "@mui/material";
import * as Yup from "yup";
import FormikRadioGroup from "@/components/forms/FormikRadioGroup";
import FormikFileUpload from "@/components/forms/FormikFileUpload";
import { FileUploadOutlinedIcon } from "@/icons";
import FormikDatePicker from "@/components/forms/FormikDatePicker";
import CardContent from "@/pages/attendanceManagement/components/CardContent";
import {
  useFetchEmployeeReportQuery,
} from "@/api/services/attendanceManagement/onsiteAttendance/onsiteAttendance";
import { toast } from "@/components/toastify";
import useFetchCommonData from "@/hooks/useFetchCommonData";
import { Grid } from "@mui/material";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import moment from "moment";
import { StyledButton } from "@/components";
import CommonDataTable from "@/components/dataTableComponents/NewDatatable";
import { DownloadIcon } from "@/icons";
import * as XLSX from 'xlsx';
import { useGetEmployeeMusterListQuery } from "@/api/services/attendance/muster/Muster";

const ALL_EMPLOYEE_VALUE = "__ALL__";

const AttendanceReport = () => {

  const [getValues, setvalues] = useState({
    empId: "",
    type: "",
    fromDate: "",
    toDate: "",
  });

  const [tableRows, setTableRows] = useState([]);

  const { data: rowDetails = [], refetch } = useFetchEmployeeReportQuery(
    {
      empId: getValues?.empId,
      type: getValues?.type,
      fromDate: getValues?.fromDate,
      toDate: getValues?.toDate,
    },
    {
      skip: !(
        (getValues?.empId || getValues?.type) &&
        getValues?.fromDate &&
        getValues?.toDate
      ),
    }
  );
  useEffect(() => {
    if (
      (getValues?.empId || getValues?.type) &&
      getValues?.fromDate &&
      getValues?.toDate
    ) {
      refetch();
    }
  }, [getValues, refetch]);

  useEffect(() => {
    if (rowDetails?.data) {
      setTableRows(rowDetails.data);
    }
  }, [rowDetails]);

  const rows = rowDetails?.data || [];

  const { data: empListDetails = [] } = useGetEmployeeMusterListQuery();
  const empListData = empListDetails?.data || [];

  const empOptions = useMemo(
    () => [
      { value: ALL_EMPLOYEE_VALUE, label: "All" },
      ...empListData.map((emp) => ({
        value: emp.empId,
        label: emp.empName,
      })),
    ],
    [empListData],
  );


  const handleSubmitAttedance = async (values, resetForm) => {
    const isAllEmployees = values?.empId === ALL_EMPLOYEE_VALUE;
    setvalues({
      empId: isAllEmployees ? "" : values?.empId,
      type: isAllEmployees ? "ALL" : "",
      fromDate: moment(values?.fromDate).format("YYYY-MM-DD"),
      toDate: moment(values?.toDate).format("YYYY-MM-DD"),
    });
  };


  // Validation Schema using Yup

  const validationSchemaTwo = Yup.object().shape({
    empId: Yup.string().required("Please Select the Employee!"),
    fromDate: Yup.date().required("Please Select the Date!"),
    toDate: Yup.date().required("Please Select the Date!"),
  });


  const handleReset = (resetForm) => {
    resetForm();
    setvalues({
      empId: "",
      type: "",
      fromDate: "",
      toDate: "",
    });
    setTableRows([]);
  };


  const downloadExcel = () => {
    // Create a new workbook and a worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(tableRows);

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Onsite Attendance");

    // Generate a binary string representation of the workbook
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a Blob from the buffer
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });

    // Create a download link and trigger the download
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'OnsiteAttendance.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  const columns = [
    { field: "empId", headerName: "Employee No", flex: 1 },
    { field: "empName", headerName: "Employee Name", flex: 1 },
    { field: "date", headerName: "Date", flex: 1 },
    { field: "inOut", headerName: "IN/OUT", flex: 1 },
    { field: "punchInOrOut", headerName: "Date IN/OUT Time", flex: 1 },
  ]


  return (
    <>
      <Box
        sx={{
          padding: (theme) => theme.spacing(2, 0),
        }}
      >
        <Formik
          initialValues={{
            empId: "",
            fromDate: "",
            toDate: "",
          }}
          enableReinitialize
          validationSchema={validationSchemaTwo}
          onSubmit={(values, { resetForm }) => {
            handleSubmitAttedance(values);
          }}
        >
          {({ values, resetForm }) => {
            const today = new Date();
            const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
            const dayBeforeYesterday = new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000);

            return (
              <Form>
                <Grid container spacing={4}>
                  <Grid item xs={12} sm={3}>
                    <FormikSelectInput
                      name="empId"
                      label="Employee"
                      options={empOptions}
                      placeholder="Select Employee"
                      required
                      search
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormikDatePicker
                      name="fromDate"
                      label="From Date"
                      required
                      views={["year", "month", "day"]}
                      format="DD/MM/YYYY"
                      placeholder="DD/MM/YYYY"
                      maxDate={dayBeforeYesterday}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormikDatePicker
                      name="toDate"
                      label="To Date"
                      required
                      views={["year", "month", "day"]}
                      format="DD/MM/YYYY"
                      placeholder="DD/MM/YYYY"
                      minDate={values?.fromDate ? new Date(values.fromDate) : undefined}
                      maxDate={yesterday}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} mt={3}>
                    <Button type="submit" variant="contained" color="primary" sx={{ mr: 2 }}>
                      Submit
                    </Button>
                    <Button
                      type="button"

                      variant="outlined"
                      color="error"
                      onClick={() => handleReset(resetForm)}
                    >
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Box>
      {/* download onsite attendance excel */}
      <StyledButton
        icon={<DownloadIcon />}
        label="Download Excel"
        onClick={() => downloadExcel("download.xlsx")}
        size="small"
        variant="outlined"
        type="button"
        disabled={
          !tableRows || tableRows.length === 0
        }
        sx={{
          mb: 2,
          borderRadius: "4px",
          color: "hsl(38,92%,50%)",
          background: "rgb(253,239,214)",
          border: "1px solid hsl(38,92%,50%)",
          "&:hover": {
            color: "hsl(38,92%,50%)",
            background: "rgb(253,239,214)",
            border: "1px solid hsl(38,92%,50%)",
          },
          ...( // if disabled, override styles
            (!tableRows || tableRows.length === 0) && {
              color: "#aaa",
              background: "#f0f0f0",
              border: "1px solid #ccc",
              cursor: "not-allowed",
            }
          ),
        }}
      />

      <CommonDataTable
        columns={columns}
        rows={tableRows}
        checkbox={false}
        search={false}
      />
    </>
  );
};

export default AttendanceReport;
